import { Box, Flex, Text, Heading } from '@moonpig/launchpad-components'
import { styled } from '@moonpig/launchpad-utils'
import React, { FC, useState } from 'react'
import { system as s } from '@moonpig/launchpad-system'
import {
  trackGAEvent,
  createGenericSelectContentEvent,
} from '@moonpig/web-core-analytics'
import { InputForm, CloseButton, BannerImage } from './components'
import { useSearchAnalytics } from '../../pages/SearchPage/analytics'
import { closeAgeBannerEvent } from '../../pages/SearchPage/events'
import { useSearchStore } from '../../store/SearchStore'

const StyledInputFilterEntryPointBanner = styled(Box)`
  height: 100%;
  position: relative;

  ${s({
    px: { xs: 4, md: 3, lg: 5, xxl: 10 },
    pb: { xs: 4, md: 3, lg: 5, xxl: 10 },
  })}
`

const StyledBannerLayout = styled(Flex)`
  ${s({
    flexDirection: { xs: 'column', md: 'row' },
    width: '100%',
    height: '100%',
    boxShadow: 2,
    borderRadius: 4,
    bgcolor: 'colorBackground07',
  })}
`

const StyledBannerContent = styled(Flex)`
  flex-direction: column;
  align-items: start;
  justify-content: center;
  width: 100%;
  height: 100%;

  ${s({
    p: { xs: 6, xxl: 8 },
    gap: 16,
  })}
`

const StyledHeading = styled(Heading)`
  ${s({
    typography: {
      xs: 'typeMobileDisplay04',
      sm: 'typeMobileDisplay03',
      md: 'typeDesktopDisplay04',
      lg: 'typeDesktopDisplay03',
    },
    color: 'colorTextInverted',
    textAlign: 'left',
    alignSelf: 'top',
    m: 0,
  })}
`

const StyledBannerDescriptionText = styled(Text)`
  ${s({
    typography: { xs: 'typeBodyText', lg: 'typeBodyLead' },
    color: 'colorTextInverted',
    textAlign: 'left',
    alignSelf: 'top',
    mb: 4,
  })}
`

type InputFilterEntryPointBannerProps = {
  title: string
  description: string
  images: {
    xs: string
    md: string
    description: string
  }
  ctaTitle: string
  filter: {
    integerId: string
    id: string
  }
}

export const InputFilterEntryPointBanner: FC<
  InputFilterEntryPointBannerProps
> = ({ title, description, images, ctaTitle, filter }) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const [isOpen, setIsOpen] = useState(true)
  const pageContext = useSearchStore(store => store.pageContext)
  const currentPageType = pageContext.type.toLowerCase()

  const handleClickClose = async () => {
    setIsOpen(false)
    // Launchpad event: apps/explore/find/src/pages/SearchPage/events.ts
    if (!isLaunchpadAnalytics)
      trackGAEvent(
        createGenericSelectContentEvent({
          eventCategory: 'close age banner',
          eventAction: `${currentPageType} results`,
          eventLabel: 'age selection entry point banner',
        }),
      )
  }

  // Legacy event: apps/explore/find/src/components/InputFilterEntryPointBanner/InputFilterEntryPointBanner.tsx
  const closeBannerClickEvent = closeAgeBannerEvent()
    .withEventData({ action: `${currentPageType} results` })
    .withContentData({
      content_type: `close age banner | ${currentPageType} results | age selection entry point banner`,
    })

  return isOpen ? (
    <StyledInputFilterEntryPointBanner>
      <CloseButton
        handleClickClose={handleClickClose}
        events={
          isLaunchpadAnalytics ? { onClick: closeBannerClickEvent } : undefined
        }
      />
      <StyledBannerLayout>
        <BannerImage images={images} />
        <StyledBannerContent width="50%">
          <Flex flexDirection="column" gap={{ xs: 0, md: 8 }}>
            <StyledHeading level="h3" as="h3">
              {title}
            </StyledHeading>
            <StyledBannerDescriptionText as="p">
              {description}
            </StyledBannerDescriptionText>
          </Flex>
          <InputForm filter={filter} ctaTitle={ctaTitle} />
        </StyledBannerContent>
      </StyledBannerLayout>
    </StyledInputFilterEntryPointBanner>
  ) : null
}
